import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useGroupStore } from '@/stores/groupStore';
import { cn } from '@/lib/utils';

const COLOR_OPTIONS = [
  '#7c3aed', '#2563eb', '#0891b2', '#059669',
  '#ca8a04', '#ea580c', '#dc2626', '#db2777',
  '#9333ea', '#4f46e5', '#0284c7', '#16a34a',
];

export function GroupSettingsPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const {
    currentGroup,
    groupInvites,
    isLoadingInvites,
    loadGroupDetail,
    loadGroupInvites,
    createGroup,
    updateGroup,
    deleteGroup,
    createInvite,
    revokeInvite,
  } = useGroupStore();

  const isEditing = !!groupId;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [hashtagsInput, setHashtagsInput] = useState('');
  const [color, setColor] = useState('#7c3aed');
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isEditing && groupId) {
      if (!currentGroup || currentGroup.id !== groupId) {
        loadGroupDetail(groupId);
      }
      loadGroupInvites(groupId);
    }
  }, [groupId]);

  useEffect(() => {
    if (isEditing && currentGroup) {
      setName(currentGroup.name);
      setDescription(currentGroup.description || '');
      setHashtagsInput(currentGroup.hashtags.join(', '));
      setColor(currentGroup.color || '#7c3aed');
      setVisibility(currentGroup.visibility);
    }
  }, [currentGroup]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const hashtags = hashtagsInput
      .split(',')
      .map((t) => t.trim().replace(/^#/, ''))
      .filter(Boolean)
      .slice(0, 5);

    try {
      if (isEditing && groupId) {
        await updateGroup(groupId, { name, description, hashtags, color, visibility });
        navigate(`/groups/${groupId}`);
      } else {
        const group = await createGroup({ name, description, hashtags, color, visibility });
        navigate(`/groups/${group.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save group');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!groupId) return;
    if (!confirm('Are you sure you want to delete this group? This cannot be undone.')) return;

    try {
      await deleteGroup(groupId);
      navigate('/groups');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete group');
    }
  };

  const handleCreateInvite = async () => {
    if (!groupId) return;
    try {
      await createInvite(groupId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invite');
    }
  };

  return (
    <div className="h-[calc(100vh-7.5rem)] overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <Link
            to={isEditing ? `/groups/${groupId}` : '/groups'}
            className="text-muted-foreground hover:text-primary transition-colors"
          >
            &larr;
          </Link>
          <h1 className="text-lg font-bold text-card-foreground">
            {isEditing ? 'Group Settings' : 'Create Group'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-4 space-y-5">
        {error && (
          <div className="p-3 bg-red-500/10 text-red-500 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={30}
            required
            placeholder="Group name"
            className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <p className="text-xs text-muted-foreground mt-1">{name.length}/30</p>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={150}
            rows={3}
            placeholder="What's this group about?"
            className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
          />
          <p className="text-xs text-muted-foreground mt-1">{description.length}/150</p>
        </div>

        {/* Hashtags */}
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-1">
            Hashtags
          </label>
          <input
            type="text"
            value={hashtagsInput}
            onChange={(e) => setHashtagsInput(e.target.value)}
            placeholder="music, art, tech (comma-separated, max 5)"
            className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>

        {/* Color */}
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-1">
            Color
          </label>
          <div className="flex flex-wrap gap-2">
            {COLOR_OPTIONS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className={cn(
                  'w-8 h-8 rounded-full transition-all',
                  color === c ? 'ring-2 ring-primary ring-offset-2 ring-offset-background scale-110' : 'hover:scale-110'
                )}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        {/* Visibility */}
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-1">
            Visibility
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setVisibility('public')}
              className={cn(
                'flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors border',
                visibility === 'public'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted text-muted-foreground border-border hover:text-foreground'
              )}
            >
              Public
            </button>
            <button
              type="button"
              onClick={() => setVisibility('private')}
              className={cn(
                'flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors border',
                visibility === 'private'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted text-muted-foreground border-border hover:text-foreground'
              )}
            >
              Private
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {visibility === 'public'
              ? 'Anyone can find and join this group'
              : 'Only invited users or approved requests can join'}
          </p>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting || !name.trim()}
          className="w-full py-3 px-4 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {isSubmitting
            ? 'Saving...'
            : isEditing
              ? 'Save Changes'
              : 'Create Group'}
        </button>

        {/* Invite links (edit mode) */}
        {isEditing && groupId && (
          <div className="border-t border-border pt-5 mt-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-card-foreground">Invite Links</h3>
              <button
                type="button"
                onClick={handleCreateInvite}
                className="text-sm px-3 py-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Create Link
              </button>
            </div>

            {isLoadingInvites && (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            )}

            {groupInvites.length === 0 && !isLoadingInvites && (
              <p className="text-sm text-muted-foreground">No invite links yet</p>
            )}

            <div className="space-y-2">
              {groupInvites.map((invite) => (
                <div
                  key={invite.id}
                  className="flex items-center justify-between p-3 bg-muted rounded-lg"
                >
                  <div>
                    <code className="text-sm font-mono text-primary">
                      {invite.inviteCode}
                    </code>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {invite.useCount} uses
                      {invite.maxUses != null && ` / ${invite.maxUses}`}
                      {invite.expiresAt && ` · Expires ${new Date(invite.expiresAt).toLocaleDateString()}`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => navigator.clipboard.writeText(invite.inviteUrl || `${window.location.origin}/invite/${invite.inviteCode}`)}
                      className="text-xs px-2 py-1 rounded bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
                    >
                      Copy Link
                    </button>
                    <button
                      type="button"
                      onClick={() => revokeInvite(groupId, invite.id)}
                      className="text-xs px-2 py-1 rounded text-red-500 hover:bg-red-500/10 transition-colors"
                    >
                      Revoke
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Delete (edit mode, owner only) */}
        {isEditing && currentGroup?.myRole === 'owner' && (
          <div className="border-t border-border pt-5">
            <button
              type="button"
              onClick={handleDelete}
              className="w-full py-3 px-4 rounded-lg bg-red-500/10 text-red-500 font-medium hover:bg-red-500/20 transition-colors"
            >
              Delete Group
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
